import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackConversion, primeAttribution } from "@/lib/conversionTracker";

/**
 * Records a `page_view` conversion event on every route change so the Metrics
 * dashboard can distinguish Page Views from Unique Visitors. First-touch
 * attribution is primed once on mount and never overwritten afterwards.
 *
 * Analytics only — renders nothing and changes no app behavior.
 */
/**
 * Redirect aliases resolve to the same canonical page — logging both legs of
 * the redirect would double-count one navigation.
 */
const PATH_ALIASES: Record<string, string> = {
  "/privacy-policy": "/privacy",
  "/terms-of-service": "/terms",
  "/terms-and-conditions": "/terms",
  "/disclaimer": "/medical-disclaimer",
};

const canonicalPath = (path: string): string => {
  const trimmed = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return PATH_ALIASES[trimmed] ?? trimmed;
};

export const ConversionPageViewTracker = () => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const lastAt = useRef<number>(0);

  useEffect(() => {
    primeAttribution();
  }, []);

  useEffect(() => {
    const path = canonicalPath(location.pathname);
    // Ignore authenticated app surfaces — marketing funnel only.
    if (path.startsWith("/admin")) return;
    if (lastPath.current === path) return;
    // Same canonical page reached through a redirect alias within one
    // navigation flow — already counted.
    if (Date.now() - lastAt.current < 2000 && lastPath.current !== null &&
        canonicalPath(lastPath.current) === path) return;
    lastPath.current = path;
    lastAt.current = Date.now();
    trackConversion("page_view", { path });
  }, [location.pathname]);

  return null;
};

export default ConversionPageViewTracker;